import { raf } from 'utils/eventHelpers';
import { setPrefixedStyle } from 'utils/DOMHelpers';

class Carousel {
  constructor(element, options) {
    this._element = element;
    this._options = Object.assign(Carousel.options, options);

    this._refs = {
      window: element.querySelector(this._options.window),
      bullets: [].slice.call(element.querySelectorAll(this._options.bullet)),
      products: [].slice.call(element.querySelectorAll(this._options.products)),
      slides: [],
      body: document.querySelector('body'),
    };

    this._refs.slides = [].slice.call(this._refs.window.children);
    this._refs.window.scrollLeft = 0;
    this._element.setAttribute('data-initialized', 'true');

    this._onTouchMove = raf(this._onTouchMove.bind(this));

    this._refs.window.addEventListener('mousedown', (e) => this._onTouchStart(e), {
      passive: true,
    });
    this._refs.window.addEventListener('touchstart', (e) => this._onTouchStart(e), {
      passive: true,
    });
    this._refs.window.addEventListener('mousemove', (e) => this._onTouchMove(e), {
      passive: true,
    });
    this._refs.window.addEventListener('touchmove', (e) => this._onTouchMove(e), {
      passive: true,
    });
    this._refs.window.addEventListener('touchend', (e) => this._onTouchEnd(e), {
      passive: true,
    });
    document.addEventListener('mouseup', (e) => this._onTouchEnd(e), {
      passive: true,
    });

    this._refs.bullets.forEach((bullet) => {
      bullet.addEventListener('click', (e) => this._onBulletClick(e));
      bullet.addEventListener('touchend', (e) => this._onBulletClick(e));
    });

    this._refs.slides.forEach((slide) =>
      slide.addEventListener('dragstart', (e) => e.preventDefault())
    );

    window.addEventListener('resize', () => {
      setTimeout(() => {
        this.goto(this._state.index);
      }, this._options.timeout);
    });

    this._state = {
      index: 0,
      dragging: false,
      currentPos: 0,
      direction: 'right',
      touchStart: {
        x: 0,
        y: 0,
      },
    };

    this.goto(0);
  }

  static options = {
    bullet: null,
    window: null,
    products: null,
    threshold: 30,
    bound: 0.66,
    breakpoint: 720,
    minmax: {
      min: 1,
      max: 2,
    },
    timeout: 300,
  };

  get slideWidth() {
    return this._refs.slides[0].offsetWidth;
  }

  // eslint-disable-next-line accessor-pairs
  set activeBullet(index) {
    this._refs.bullets.forEach((bullet, i) => {
      bullet.setAttribute('data-selected', index === i);
    });
  }

  // eslint-disable-next-line accessor-pairs
  set activeProduct(index) {
    this._refs.products.forEach((product, i) => {
      product.setAttribute('data-active', index === i);
    });
  }

  _onBulletClick(e) {
    e.preventDefault();
    const index = this._refs.bullets.indexOf(e.target);
    if (index !== -1) this.goto(index);
  }

  _onTouchStart(e) {
    if (this._state.dragging) return;
    this._state.dragging = true;

    const pointer = e.changedTouches ? e.changedTouches[0] : e;
    this._state.touchStart = {
      x: pointer.pageX,
      y: pointer.pageY,
    };
  }
  // eslint-disable-next-line complexity
  _onTouchMove(e) {
    if (!this._state.dragging) return;

    const pointer = e.changedTouches ? e.changedTouches[0] : e;
    const d = {
      x: pointer.pageX - this._state.touchStart.x,
      y: pointer.pageY - this._state.touchStart.y,
    };

    if (Math.abs(d.x) > Math.abs(d.y)) {
      const offset = -this._state.index * this.slideWidth + d.x;
      const isWithinBounds =
        offset >= this.slideWidth * -(this._refs.slides.length - 1) && offset <= 0;
      const dir = offset / Math.abs(offset);
      const target = isWithinBounds
        ? offset
        : offset - d.x * this._options.bound + dir * Math.sqrt(Math.abs(d.x) * this._options.bound);

      this._refs.slides.forEach((slide) => {
        setPrefixedStyle(slide, 'transform', `translateX(${target}px)`);
        setPrefixedStyle(slide, 'transition-duration', '.1s');
      });
    } else {
      this.slideTo(this.index);
    }
  }
  // eslint-disable-next-line complexity
  _onTouchEnd(e) {
    if (!this._state.dragging) return;
    this._state.dragging = false;

    const pointer = e.changedTouches ? e.changedTouches[0] : e;
    const d = {
      x: pointer.pageX - this._state.touchStart.x,
      y: pointer.pageY - this._state.touchStart.y,
    };

    if (Math.abs(d.x) > Math.abs(d.y) && Math.abs(d.x) > this._options.threshold) {
      // eslint-disable-next-line no-unused-expressions
      d.x > 0 ? this.previous() : this.next();
    } else {
      this.slideTo(this._state.index);
    }
  }

  goto(index) {
    const max = this._refs.slides.length - 1;
    const i = Math.min(Math.max(index, 0), max);

    this._refs.slides[this._state.index].removeAttribute('data-active');
    this._refs.slides[i].setAttribute('data-active', 'true');

    this.slideTo(i);
    this.activeBullet = i;
    this.activeProduct = i;
    this._state.index = i;
  }

  previous() {
    this.goto(this._state.index - 1);
  }

  next() {
    this.goto(this._state.index + 1);
  }

  slideTo(index) {
    const target = -index * this.slideWidth;
    // eslint-disable-next-line no-unused-expressions
    target <= this._state.currentPos
      ? (this._state.direction = 'left')
      : (this._state.direction = 'right');
    this._state.currentPos = target;
    this._refs.bullets.forEach((bullet) => {
      bullet.setAttribute('data-direction', this._state.direction);
    });
    this._refs.slides.forEach((slide) => {
      setPrefixedStyle(slide, 'transform', `translateX(${target}px)`);
      setPrefixedStyle(slide, 'transition', '');
    });
  }
}

export default Carousel;
